import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getRegulatoryPolicy } from '@core/regulatory';
import { getLanguage, type SupportedLanguage } from './language';

export type NumberFormatOptions = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  currency?: string;
};

/** UI language → BCP 47 locale for grouping (display formatters only). */
const LANGUAGE_NUMBER_LOCALE: Record<SupportedLanguage, string> = {
  en: 'en-GB',
  es: 'es-ES',
  de: 'de-DE',
  fr: 'fr-FR',
  it: 'it-IT',
  el: 'el-GR',
  bg: 'bg-BG',
  da: 'da-DK',
  fi: 'fi-FI',
  id: 'id-ID',
  ja: 'ja-JP',
  ko: 'ko-KR',
  lv: 'lv-LV',
  lt: 'lt-LT',
  ms: 'ms-MY',
  nb: 'nb-NO',
  pl: 'pl-PL',
  pt: 'pt-PT',
  ro: 'ro-RO',
  ru: 'ru-RU',
  sr: 'sr-RS',
  sv: 'sv-SE',
  th: 'th-TH',
  tr: 'tr-TR',
  vi: 'vi-VN',
  zh: 'zh-CN',
  'zh-TW': 'zh-TW',
};

export const COMMA_DECIMAL_LANGUAGE_CONFIG: Record<string, boolean> = {
  es: true, // Spanish
  de: true, // German
  fr: true, // French
  it: true, // Italian
  el: true, // Greek
  bg: true, // Bulgarian
  da: true, // Danish
  fi: true, // Finnish
  id: true, // Indonesian
  lv: true, // Latvian
  lt: true, // Lithuanian
  nb: true, // Norwegian Bokmål
  pl: true, // Polish
  pt: true, // Portuguese
  ro: true, // Romanian
  ru: true, // Russian
  sr: true, // Serbian
  sv: true, // Swedish
  tr: true, // Turkish
  vi: true, // Vietnamese
};

export function usesCommaDecimal(
  lang: SupportedLanguage = getLanguage()
): boolean {
  return COMMA_DECIMAL_LANGUAGE_CONFIG[lang] === true;
}

export function getLanguageDecimalSeparator(
  lang: SupportedLanguage = getLanguage()
): ',' | '.' {
  return usesCommaDecimal(lang) ? ',' : '.';
}

const DEFAULT_FRACTION_DIGITS = 2;

const formatterCache = new Map<string, Intl.NumberFormat>();

function getCachedFormatter(
  locale: string,
  options: Intl.NumberFormatOptions
): Intl.NumberFormat {
  const key = `${locale}:${JSON.stringify(options)}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, formatter);
  }
  return formatter;
}

export function resolveNumberLocale(_currency?: string): string {
  const lang = getLanguage();
  return LANGUAGE_NUMBER_LOCALE[lang] ?? LANGUAGE_NUMBER_LOCALE.en;
}

export function getNumberSeparators(_currency?: string): {
  decimal: string;
  group: string;
} {
  const decimal = getLanguageDecimalSeparator();
  return {
    decimal,
    group: decimal === ',' ? '.' : ',',
  };
}

export function truncateToDecimals(value: number, decimals = 2): number {
  const factor = 10 ** decimals;
  return Math.floor((value + 0.00001) * factor) / factor;
}

export function formatDecimal(
  value: number | null | undefined,
  options: NumberFormatOptions = {}
): string {
  const minimumFractionDigits =
    options.minimumFractionDigits ?? DEFAULT_FRACTION_DIGITS;
  const maximumFractionDigits =
    options.maximumFractionDigits ?? DEFAULT_FRACTION_DIGITS;
  const locale = resolveNumberLocale(options.currency);
  const formatter = getCachedFormatter(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  if (value === null || value === undefined || Number.isNaN(value)) {
    return formatter.format(0);
  }

  return formatter.format(truncateToDecimals(value, maximumFractionDigits));
}

/** Monetary amounts with 2 decimal places. */
export function formatAmount(
  value: number | null | undefined,
  currency?: string
): string {
  return formatDecimal(value, { currency });
}

/**
 * Monetary amount with currency symbol and position from Intl
 * (locale + ISO currency code). Reuses the same locale / truncate /
 * formatter-cache path as formatAmount — no per-currency hardcoding.
 *
 * Use for displays that should show `$100.00` / `100.00 kr` rather than
 * a bare number with a separate currency code (Balance keeps formatAmount).
 */
export function formatCurrencyAmount(
  value: number | null | undefined,
  currency?: string,
  options: { showSign?: boolean } = {}
): string {
  const amount =
    value === null || value === undefined || Number.isNaN(value) ? 0 : value;
  const code =
    typeof currency === 'string' && currency.length === 3 && currency !== 'CHIP'
      ? currency.toUpperCase()
      : null;

  if (!code) {
    const plain = formatAmount(amount, currency);
    if (options.showSign && amount > 0) return `+${plain}`;
    return plain;
  }

  const locale = resolveNumberLocale(code);
  const truncated = truncateToDecimals(amount, DEFAULT_FRACTION_DIGITS);

  try {
    const formatter = getCachedFormatter(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
      ...(options.showSign ? { signDisplay: 'exceptZero' } : {}),
    });

    if (code === 'CAD') {
      return formatter
        .formatToParts(truncated)
        .map(part => (part.type === 'currency' ? 'C$' : part.value))
        .join('');
    }
    return formatter.format(truncated);
  } catch {
    // Unknown / non-ISO code — keep number formatting, append code.
    const plain = formatAmount(amount, currency);
    if (options.showSign && amount > 0) return `+${plain} ${code}`;
    return `${plain} ${code}`;
  }
}

/** Whether the active jurisdiction shows currency symbols (not ISO codes). */
export function shouldUseCurrencySymbol(): boolean {
  return getRegulatoryPolicy().useCurrencySymbol;
}

/**
 * Narrow currency symbol from Intl (e.g. €, $, kr). Falls back to the code
 * when the currency is unknown / non-ISO / CHIP.
 */
export function getCurrencyNarrowSymbol(currency?: string): string {
  if (!currency || currency === 'CHIP') return currency ?? '';
  const code = currency.length === 3 ? currency.toUpperCase() : null;
  if (!code) return currency;
  if (code === 'CAD') return 'C$';

  try {
    const parts = new Intl.NumberFormat(resolveNumberLocale(code), {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(1);
    return parts.find(part => part.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}

/**
 * Unit for labels such as "CREDIT {{currency}}" / "WIN {{currency}}":
 * symbol when policy allows, otherwise the ISO code (Spain).
 */
export function formatCurrencyUnit(currency?: string): string {
  if (!currency) return '';
  if (currency === 'CHIP') return currency;
  if (!shouldUseCurrencySymbol()) return currency;
  return getCurrencyNarrowSymbol(currency);
}

/**
 * Full monetary display for UI. Symbol jurisdictions use Intl positioning
 * (`€10.00` / `10.00 kr`); Spain keeps `10.00 EUR`.
 * Display-only — does not change numeric values or wallet math.
 */
export function formatMoney(
  value: number | null | undefined,
  currency?: string,
  options: { showSign?: boolean } = {}
): string {
  if (shouldUseCurrencySymbol()) {
    return formatCurrencyAmount(value, currency, options);
  }

  const amount =
    value === null || value === undefined || Number.isNaN(value) ? 0 : value;
  const plain = formatAmount(amount, currency);
  const code =
    typeof currency === 'string' && currency.length > 0 ? currency : '';

  if (!code) {
    if (options.showSign && amount > 0) return `+${plain}`;
    return plain;
  }

  if (options.showSign && amount > 0) return `+${plain} ${code}`;
  return `${plain} ${code}`;
}

/** Multiplier display (e.g. `1,234.50x` / `1.234,50x`). */
export function formatMultiplier(
  value: number | null | undefined,
  currency?: string
): string {
  return `${formatDecimal(value, { currency })}x`;
}

export function formatEditableAmount(value: number, currency?: string): string {
  const { decimal, group } = getNumberSeparators(currency);
  const normalized = truncateToDecimals(value).toFixed(DEFAULT_FRACTION_DIGITS);
  const [integerPart, fractionPart = '00'] = normalized.split('.');
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  return `${groupedInteger}${decimal}${fractionPart}`;
}

export function canonicalizeLocalizedNumberInput(
  input: string,
  currency?: string
): string {
  const { decimal } = getNumberSeparators(currency);
  if (decimal === '.' || input.includes(decimal) || !input.includes('.')) {
    return input;
  }

  const lastDot = input.lastIndexOf('.');
  return `${input.slice(0, lastDot)}${decimal}${input.slice(lastDot + 1)}`;
}

export function isLocalizedDecimalKey(key: string, currency?: string): boolean {
  const { decimal } = getNumberSeparators(currency);
  if (key === decimal) return true;
  return decimal !== '.' && key === '.';
}

export function acceptLocalizedNumberInput(
  input: string,
  currency?: string
): string | null {
  const canonical = canonicalizeLocalizedNumberInput(input, currency);
  return isPartialLocalizedNumberInput(canonical, currency) ? canonical : null;
}

export function parseLocalizedNumber(
  input: string,
  currency?: string
): number | null {
  const trimmed = canonicalizeLocalizedNumberInput(input, currency).trim();
  if (!trimmed) return null;

  const { decimal, group } = getNumberSeparators(currency);
  let normalized = trimmed.split(group).join('');

  if (decimal !== '.') {
    const lastDecimalIndex = normalized.lastIndexOf(decimal);
    if (lastDecimalIndex >= 0) {
      const integerPart = normalized
        .slice(0, lastDecimalIndex)
        .replace(/[^\d]/g, '');
      const fractionPart = normalized
        .slice(lastDecimalIndex + 1)
        .replace(/[^\d]/g, '');
      normalized = `${integerPart}.${fractionPart}`;
    } else {
      normalized = normalized.replace(/[^\d]/g, '');
    }
  }

  const parsed = Number.parseFloat(normalized);
  if (Number.isNaN(parsed) || parsed < 0) return null;
  return parsed;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function isPartialLocalizedNumberInput(
  input: string,
  _currency?: string
): boolean {
  if (input === '') return true;

  const canonical = canonicalizeLocalizedNumberInput(input);
  const { decimal, group } = getNumberSeparators();
  const dec = escapeRegExp(decimal);
  const grp = escapeRegExp(group);
  const pattern = new RegExp(`^\\d*(${grp}\\d{3})*(${dec}\\d*)?$`);
  return pattern.test(canonical);
}

export function useNumberFormat(currency?: string) {
  const { i18n } = useTranslation();

  return useMemo(
    () => ({
      locale: resolveNumberLocale(),
      separators: getNumberSeparators(),
      usesCommaDecimal: usesCommaDecimal(),
      formatDecimal: (
        value: number | null | undefined,
        options: Omit<NumberFormatOptions, 'currency'> = {}
      ) => formatDecimal(value, { ...options, currency }),
      formatAmount: (value: number | null | undefined) =>
        formatAmount(value, currency),
      formatCurrencyAmount: (
        value: number | null | undefined,
        options?: { showSign?: boolean }
      ) => formatCurrencyAmount(value, currency, options),
      formatMoney: (
        value: number | null | undefined,
        options?: { showSign?: boolean }
      ) => formatMoney(value, currency, options),
      formatCurrencyUnit: () => formatCurrencyUnit(currency),
      shouldUseCurrencySymbol: () => shouldUseCurrencySymbol(),
      formatEditableAmount: (value: number) => formatEditableAmount(value),
      canonicalizeLocalizedNumberInput: (input: string) =>
        canonicalizeLocalizedNumberInput(input),
      acceptLocalizedNumberInput: (input: string) =>
        acceptLocalizedNumberInput(input),
      isLocalizedDecimalKey: (key: string) => isLocalizedDecimalKey(key),
      parseLocalizedNumber: (input: string) => parseLocalizedNumber(input),
      isPartialLocalizedNumberInput: (input: string) =>
        isPartialLocalizedNumberInput(input),
    }),
    [currency, i18n.language]
  );
}
